import { Injectable } from '@angular/core';
// import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
// import { Observable } from 'rxjs';
// import { finalize } from 'rxjs/operators';
import axios from 'axios';
import { parse, visit, print, FieldNode } from 'graphql';
import { LoaderService } from './loader.service';
import { DataShareService } from '../data-share.service';
import { AccountMembership } from 'app/API.service.custom';
import { shouldPreserveWabaPhoneNumberField } from 'app/core/phone-number-masking-context';
import { hasGraphQLMutationPayload, ReadOnlyAccessError } from 'app/core/read-only-access';

type AccountMembershipWithPermissions = AccountMembership & {
  mask_number_permission?: boolean;
};

@Injectable({
  providedIn: 'root'
})

export class InterceptorService {

  private maskPhoneNumberEnabled = false;
  private readOnlyAccessEnabled = false;

  constructor(public loaderService: LoaderService, private dataShareService: DataShareService) {
    this.dataShareService.getActiveAccount().subscribe(account => {
      const accountWithPermissions = account as AccountMembershipWithPermissions | null;
      this.maskPhoneNumberEnabled = !!accountWithPermissions?.mask_number_permission;
      this.readOnlyAccessEnabled = account?.isReadOnlyAccess === true;
    });
  }

  intercept() {
    axios.interceptors.request.use(request => {
      if (this.readOnlyAccessEnabled &&
          (hasGraphQLMutationPayload(request.data) || hasGraphQLMutationPayload(request.params))) {
        return Promise.reject(new ReadOnlyAccessError());
      }
      console.log("INTERCEPTED!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!");
      this.loaderService.isLoading.next(true);
      if (this.maskPhoneNumberEnabled) {
        this.removeMaskedFieldsFromProjection(request);
      }
      return request;
    });

    axios.interceptors.response.use(response => {
      this.loaderService.isLoading.next(false);
      return response;
    }, error => {
      this.loaderService.isLoading.next(false);
      return Promise.reject(error);
    });
}
  // intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
  //   console.log("INTERCEPTED!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!");
  //   this.loaderService.isLoading.next(true);

  //   return next.handle(req).pipe(
  //     finalize(
  //       () => {
  //         this.loaderService.isLoading.next(false);
  //       }
  //     )
  //   );
  // }

  private removeMaskedFieldsFromProjection(request: any): void {
    const rawPayload = request?.data;
    if (!rawPayload) {
      return;
    }
    const queryText = this.extractQueryText(rawPayload);
    if (!queryText || !this.isQueryOperation(queryText)) {
      return;
    }

    const sanitizedQuery = this.sanitizeGraphQLQuery(queryText);

    if (typeof rawPayload === 'string' && !this.looksLikeJson(rawPayload.trim())) {
      if (sanitizedQuery && sanitizedQuery !== rawPayload) {
        request.data = sanitizedQuery;
      }
      return;
    }

    const { parsed, wasString } = this.parsePayload(rawPayload);
    if (!parsed) {
      return;
    }

    if (sanitizedQuery && typeof parsed.query === 'string' && parsed.query !== sanitizedQuery) {
      parsed.query = sanitizedQuery;
    }

    this.stripProjectionFields(parsed);
    if (wasString) {
      try {
        request.data = JSON.stringify(parsed);
      } catch (err) {
        console.warn('Failed to serialize masked payload', err);
      }
    }
  }

  private extractQueryText(payload: any): string | null {
    if (!payload) {
      return null;
    }
    if (typeof payload === 'string') {
      const trimmed = payload.trim();
      if (!trimmed) {
        return null;
      }
      if (this.looksLikeJson(trimmed)) {
        const parsed = this.tryParseJson(trimmed);
        return parsed ? this.extractQueryText(parsed) : null;
      }
      return trimmed;
    }
    if (typeof payload.query === 'string') {
      return payload.query;
    }
    return null;
  }

  private parsePayload(payload: any): { parsed: any | null; wasString: boolean } {
    if (payload == null) {
      return { parsed: null, wasString: false };
    }
    if (typeof payload === 'string') {
      const trimmed = payload.trim();
      if (this.looksLikeJson(trimmed)) {
        const parsed = this.tryParseJson(trimmed);
        return { parsed, wasString: true };
      }
      return { parsed: null, wasString: true };
    }
    if (typeof payload === 'object') {
      return { parsed: payload, wasString: false };
    }
    return { parsed: null, wasString: false };
  }

  private looksLikeJson(value: string): boolean {
    return value.startsWith('{') || value.startsWith('[');
  }

  private tryParseJson(value: string): any | null {
    try {
      return JSON.parse(value);
    } catch (err) {
      console.warn('Failed to parse request payload for masking', err);
      return null;
    }
  }

  private isQueryOperation(queryText: string): boolean {
    const trimmed = queryText?.trim();
    if (!trimmed) {
      return false;
    }
    const normalized = trimmed.toLowerCase();
    const braceIndex = normalized.indexOf('{');
    const prefix = braceIndex === -1 ? normalized : normalized.slice(0, braceIndex);
    return !prefix.includes('mutation');
  }

  private sanitizeGraphQLQuery(queryText: string | null): string | null {
    if (!queryText) {
      return null;
    }
    try {
      let mutated = false;
      const document = parse(queryText);
      const stripped = visit(document, {
        Field: (node, _key, _parent, _path, ancestors) => {
          if (this.shouldRemoveFieldNode(node as FieldNode, ancestors)) {
            mutated = true;
            return null;
          }
          return undefined;
        },
      });
      return mutated ? print(stripped) : queryText;
    } catch (err) {
      console.warn('Failed to parse GraphQL query for masking', err);
      return queryText;
    }
  }

  private shouldRemoveFieldNode(node: FieldNode, ancestors: ReadonlyArray<any> = []): boolean {
    if (!node || !node.name) {
      return false;
    }
    const fieldName = node.name.value;
    if (!fieldName) {
      return false;
    }
    if (shouldPreserveWabaPhoneNumberField(node, ancestors)) {
      return false;
    }
    return this.isSensitiveProperty(fieldName);
  }

  private stripProjectionFields(target: any): void {
    if (!target) {
      return;
    }
    if (Array.isArray(target)) {
      target.forEach(item => this.stripProjectionFields(item));
      return;
    }
    if (typeof target !== 'object') {
      return;
    }

    Object.keys(target).forEach(key => {
      const value = target[key];
      if (key === 'projection' && Array.isArray(value)) {
        const filtered = value.filter(entry => !this.shouldRemoveProjectionEntry(entry));
        if (filtered.length !== value.length) {
          target[key] = filtered;
        }
        filtered.forEach(entry => this.stripProjectionFields(entry));
      } else {
        this.stripProjectionFields(value);
      }
    });
  }

  private shouldRemoveProjectionEntry(entry: any): boolean {
    if (!entry) {
      return false;
    }
    if (typeof entry === 'string') {
      return this.isSensitiveProperty(entry);
    }
    if (Array.isArray(entry)) {
      return entry.some(item => this.shouldRemoveProjectionEntry(item));
    }
    if (typeof entry === 'object') {
      if (this.isSensitiveProperty((entry as any).property)) {
        return true;
      }
      const node = entry.node || entry.Node;
      if (node && (this.isSensitiveProperty(node.property) || this.isSensitiveProperty(node.propertyName))) {
        return true;
      }
      return false;
    }
    return false;
  }

  private isSensitiveProperty(value: any): boolean {
    if (typeof value !== 'string') {
      return false;
    }
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return false;
    }
    if (normalized === 'phonenumber' || normalized === 'contactnumberdetail') {
      return true;
    }
    return normalized.startsWith('contactnumberdetail__') || normalized.startsWith('contactnumberdetail.');
  }
}

export function AxiosConfigFactory(axiosIntercept: InterceptorService): any {
  return () => axiosIntercept.intercept();
}
